// Two requirements on `callback`, both inherited from observing raw
// MutationRecords:
//
// 1. It must not add nodes matching `selector`. The observer sees its
//    own callback's DOM writes and re-enters — a self-feeding loop.
//    (MutationSummary, which these helpers replaced, defaulted to
//    observeOwnChanges: false and silently discarded them, so this
//    invariant is newly load-bearing.)
// 2. It must be idempotent. The same element reaches `callback` more than
//    once by several routes, including:
//
//    Attach-then-populate. container.appendChild(wrapper) followed by
//    wrapper.appendChild(el) queues two records in one batch, and the
//    querySelectorAll below runs at callback time -- by which point
//    wrapper already holds el. Both records deliver el. That's ordinary
//    hand-written JS: no framework, no reparent.
//
//    An ancestor moved. querySelectorAll runs against every added node,
//    so moving any wrapper that contains a match re-delivers the match
//    without the matching node itself being touched. A single delivering
//    record, and no dependence on batch composition.
//
//    The match itself moved. A move queues two records -- the old
//    parent's carries the node in removedNodes, the new parent's in
//    addedNodes -- so the addition re-fires the callback. Cart drawers and
//    section re-renders produce this and the previous route both.
//
//    Callers get there variously: a marker attribute, an already-applied
//    check, a set-to-a-constant, or an API that no-ops on repeat
//    (IntersectionObserver#observe). Pick whichever fits, but pick one.
//    The failure mode is quiet -- stacked listeners and undisconnected
//    observers, not an exception.
//
// Neither helper ever passes `root` itself to `callback`: the initial
// sweep uses querySelectorAll (descendants only), and a childList record
// belongs to the mutated parent's target, which for `root` lies outside
// the observed subtree.
//
// All three delivery paths pass `callback` exactly one argument. The two
// that iterate a NodeList use for...of rather than forEach(callback),
// because NodeList#forEach passes (element, index, NodeList): a callback
// with a second parameter would silently receive an index, and receive it
// from some paths and not others. Don't shorten them back. (The matches()
// branch is a single call and never had the hazard.)
export const onElementsAdded = (selector, callback, { root = document.body } = {}) => {
  const handleMutations = mutations =>
    mutations.forEach(mutation => {
      if (mutation.type != 'childList') return

      mutation.addedNodes.forEach(node => {
        if (node.nodeType != Node.ELEMENT_NODE) return

        if (node.matches(selector)) callback(node)
        for (const match of node.querySelectorAll(selector)) callback(match)
      })
    })

  const mutationObserver = new MutationObserver(handleMutations)
  mutationObserver.observe(root, { childList: true, subtree: true })
  return mutationObserver
}

export const onElementsAvailable = (selector, callback, { root = document.body } = {}) => {
  for (const element of root.querySelectorAll(selector)) callback(element)
  return onElementsAdded(selector, callback, { root })
}
